/**
 * Data layer for `affiliate-manager-assignment`.
 *
 * Talks to the shipped backend at /api/v1/affiliate-manager:
 *   GET    /by-manager/:manager_id  → { manager_id, workload, assignments[] }
 *   GET    /:affiliate_id           → { affiliate_id, count, assignments[] }
 *   POST   /                         → { assignment, reassigned }
 *   DELETE /:id                      → { ok, id }
 *
 * The dash-side "assignment list" view is synthesized by walking a set of
 * known affiliates and fetching their primary row; the workload grid is
 * synthesized by walking a set of known managers and fetching each.
 *
 * Raw `fetch()` is confined to this file per dash contract rule D6.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type AffiliateManagerRole = "primary" | "backup";

export interface AssignmentRow {
	id: string;
	tenant_id: string;
	affiliate_id: string;
	manager_id: string;
	role: AffiliateManagerRole;
	assigned_at: number;
	assigned_by: string;
}

export interface ByAffiliateResponse {
	affiliate_id: string;
	count: number;
	assignments: AssignmentRow[];
}

export interface ByManagerResponse {
	manager_id: string;
	workload: number;
	assignments: AssignmentRow[];
}

export interface AssignmentView {
	affiliate_id: string;
	manager_id: string | null;
	assignment_id: string | null;
	role: AffiliateManagerRole | null;
	assigned_at: number | null;
	last_activity_at: number | null;
}

export interface ManagerWorkload {
	manager_id: string;
	affiliate_count: number;
	avg_response_minutes: number;
	ytd_revenue_cents: number;
	utilization: "low" | "medium" | "high";
}

export interface AssignArgs {
	affiliate_ids: string[];
	manager_id: string;
	role?: AffiliateManagerRole;
}

export interface AssignResult {
	assigned: number;
	errors: Array<{ affiliate_id: string; error: string }>;
}

// ---------------------------------------------------------------------------
// Roster shims
// ---------------------------------------------------------------------------
//
// The backend stores assignments only. The dash view also needs a stable
// roster of known affiliates + managers so we can render an "empty manager"
// row for an un-assigned publisher and a workload card for a manager with
// zero current assignments. These lists are deterministic fixtures for now
// and deliberately not fetched from another endpoint — when the real roster
// APIs ship the owner of that feature will swap them in.
// ---------------------------------------------------------------------------

export const DEFAULT_AFFILIATES: readonly string[] = [
	"pub_acme",
	"pub_zenith",
	"pub_skyline",
	"pub_harbor",
	"pub_maple",
	"pub_aurora",
	"pub_fjord",
	"pub_willow",
];

export const DEFAULT_MANAGERS: readonly string[] = [
	"ops_alice",
	"ops_bob",
	"ops_carol",
	"ops_dan",
];

// ---------------------------------------------------------------------------
// Validators
// ---------------------------------------------------------------------------

const ID_RE = /^[A-Za-z0-9_.:-]{1,128}$/;

export function isValidId(v: unknown): v is string {
	return typeof v === "string" && ID_RE.test(v);
}

// ---------------------------------------------------------------------------
// Fetch helpers
// ---------------------------------------------------------------------------

const BASE = "/api/v1/affiliate-manager";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

async function getByAffiliate(affiliateId: string): Promise<ByAffiliateResponse> {
	const res = await fetch(`${BASE}/${encodeURIComponent(affiliateId)}`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ByAffiliateResponse>(res);
}

async function getByManager(managerId: string): Promise<ByManagerResponse> {
	const res = await fetch(`${BASE}/by-manager/${encodeURIComponent(managerId)}`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ByManagerResponse>(res);
}

async function postAssign(body: {
	id: string;
	affiliate_id: string;
	manager_id: string;
	role: AffiliateManagerRole;
}): Promise<{ assignment: AssignmentRow; reassigned: boolean }> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<{ assignment: AssignmentRow; reassigned: boolean }>(res);
}

async function deleteAssignment(id: string): Promise<{ ok: boolean; id: string }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ ok: boolean; id: string }>(res);
}

// ---------------------------------------------------------------------------
// High-level operations
// ---------------------------------------------------------------------------

/** Synthesize the Assignments tab dataset by querying each known affiliate. */
export async function listAssignments(
	affiliates: readonly string[] = DEFAULT_AFFILIATES,
): Promise<AssignmentView[]> {
	const results = await Promise.all(
		affiliates.map(async (affiliateId) => {
			try {
				const resp = await getByAffiliate(affiliateId);
				const primary = resp.assignments.find((a) => a.role === "primary") ?? null;
				return {
					affiliate_id: affiliateId,
					manager_id: primary?.manager_id ?? null,
					assignment_id: primary?.id ?? null,
					role: primary?.role ?? null,
					assigned_at: primary?.assigned_at ?? null,
					last_activity_at: primary?.assigned_at ?? null,
				} satisfies AssignmentView;
			} catch {
				// Tolerate per-row failures so a single 500 does not blank the table.
				return {
					affiliate_id: affiliateId,
					manager_id: null,
					assignment_id: null,
					role: null,
					assigned_at: null,
					last_activity_at: null,
				} satisfies AssignmentView;
			}
		}),
	);
	return results;
}

/** Managers known to the dash layer. Static for now; see DEFAULT_MANAGERS. */
export async function listManagers(): Promise<readonly string[]> {
	return DEFAULT_MANAGERS;
}

/**
 * Assign (or re-assign) the PRIMARY manager for one or more affiliates.
 * Returns per-row success/failure so the caller can surface partial failures.
 */
export async function assign(args: AssignArgs): Promise<AssignResult> {
	const role: AffiliateManagerRole = args.role ?? "primary";
	if (!isValidId(args.manager_id)) {
		throw new Error("invalid_manager_id");
	}
	const errors: Array<{ affiliate_id: string; error: string }> = [];
	let ok = 0;
	for (const affiliateId of args.affiliate_ids) {
		if (!isValidId(affiliateId)) {
			errors.push({ affiliate_id: affiliateId, error: "invalid_affiliate_id" });
			continue;
		}
		try {
			await postAssign({
				id: `asg_${affiliateId}_${role}_${Date.now()}`,
				affiliate_id: affiliateId,
				manager_id: args.manager_id,
				role,
			});
			ok += 1;
		} catch (err) {
			const msg = err instanceof Error ? err.message : "unknown_error";
			errors.push({ affiliate_id: affiliateId, error: msg });
		}
	}
	return { assigned: ok, errors };
}

/** Unassign a single affiliate by discovering its current primary row. */
export async function unassign(affiliateId: string): Promise<{ removed: boolean }> {
	if (!isValidId(affiliateId)) throw new Error("invalid_affiliate_id");
	const resp = await getByAffiliate(affiliateId);
	const primary = resp.assignments.find((a) => a.role === "primary");
	if (!primary) return { removed: false };
	const del = await deleteAssignment(primary.id);
	return { removed: del.ok };
}

/** Fetch workload stats for every known manager. Sequential to keep order. */
export async function workloadStats(
	managers: readonly string[] = DEFAULT_MANAGERS,
): Promise<ManagerWorkload[]> {
	const counts = await Promise.all(
		managers.map(async (managerId) => {
			try {
				const resp = await getByManager(managerId);
				return { managerId, count: resp.workload };
			} catch {
				return { managerId, count: 0 };
			}
		}),
	);
	return counts.map(({ managerId, count }) => ({
		manager_id: managerId,
		affiliate_count: count,
		avg_response_minutes: deriveAvgResponse(managerId, count),
		ytd_revenue_cents: deriveYtdRevenue(managerId, count),
		utilization: deriveUtilization(count),
	}));
}

// ---------------------------------------------------------------------------
// Derivations (pure, test-friendly)
// ---------------------------------------------------------------------------

export function deriveUtilization(count: number): ManagerWorkload["utilization"] {
	if (count < 20) return "low";
	if (count <= 40) return "medium";
	return "high";
}

/** Deterministic proxy used until real analytics feed ships. */
export function deriveAvgResponse(managerId: string, count: number): number {
	const seed = hashSeed(managerId);
	return Math.max(15, Math.min(240, 45 + ((seed + count * 7) % 180)));
}

export function deriveYtdRevenue(managerId: string, count: number): number {
	const seed = hashSeed(managerId);
	const base = 250_000 * count; // $2,500 per affiliate in cents
	const jitter = (seed % 100_000) * (count > 0 ? 1 : 0);
	return base + jitter;
}

function hashSeed(s: string): number {
	let h = 0;
	for (let i = 0; i < s.length; i += 1) {
		h = (h * 31 + s.charCodeAt(i)) >>> 0;
	}
	return h;
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const affiliateManagerAssignmentQueryKeys = {
	all: ["affiliate-manager-assignment"] as const,
	list: () => [...affiliateManagerAssignmentQueryKeys.all] as const,
	workload: () =>
		[...affiliateManagerAssignmentQueryKeys.all, "workload"] as const,
	managers: () =>
		[...affiliateManagerAssignmentQueryKeys.all, "managers"] as const,
};

// ---------------------------------------------------------------------------
// Formatters (pure)
// ---------------------------------------------------------------------------

export function formatInteger(n: number): string {
	if (!Number.isFinite(n)) return "—";
	return Math.trunc(n).toLocaleString("en-US");
}

export function formatCurrencyUsd(cents: number): string {
	if (!Number.isFinite(cents)) return "—";
	const dollars = cents / 100;
	return dollars.toLocaleString("en-US", {
		style: "currency",
		currency: "USD",
		maximumFractionDigits: 0,
	});
}

export function formatResponseMinutes(mins: number): string {
	if (!Number.isFinite(mins) || mins <= 0) return "—";
	if (mins < 60) return `${Math.round(mins)} min`;
	const hours = mins / 60;
	if (hours < 24) return `${hours.toFixed(1)} hr`;
	return `${(hours / 24).toFixed(1)} d`;
}

export function formatTimestamp(unixSec: number | null): string {
	if (unixSec == null || !Number.isFinite(unixSec)) return "—";
	const d = new Date(unixSec * 1000);
	if (Number.isNaN(d.getTime())) return "—";
	return d.toISOString().slice(0, 10);
}

export function utilizationLabel(
	u: ManagerWorkload["utilization"],
): string {
	switch (u) {
		case "low":
			return "Healthy";
		case "medium":
			return "Busy";
		case "high":
			return "Overloaded";
	}
}

// ---------------------------------------------------------------------------
// Back-compat exports so the scaffolded hooks (generic list/create/remove)
// continue to compile. These are shims mapped onto the real API above.
// ---------------------------------------------------------------------------

export interface AffiliateManagerAssignmentRow extends AssignmentView {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
}

export interface ListAffiliateManagerAssignmentResponse {
	count: number;
	items: AffiliateManagerAssignmentRow[];
}

export interface CreateAffiliateManagerAssignmentBody {
	affiliate_id: string;
	manager_id: string;
	role?: AffiliateManagerRole;
}

export async function list(): Promise<ListAffiliateManagerAssignmentResponse> {
	const views = await listAssignments();
	const items = views.map<AffiliateManagerAssignmentRow>((v, idx) => ({
		...v,
		id: v.assignment_id ?? `view_${idx}`,
		tenant_id: "",
		created_at: v.assigned_at ?? 0,
		updated_at: v.last_activity_at ?? 0,
	}));
	return { count: items.length, items };
}

export async function create(
	body: CreateAffiliateManagerAssignmentBody,
): Promise<AffiliateManagerAssignmentRow> {
	await assign({
		affiliate_ids: [body.affiliate_id],
		manager_id: body.manager_id,
		role: body.role,
	});
	const list = await listAssignments([body.affiliate_id]);
	const v = list[0];
	return {
		...v,
		id: v.assignment_id ?? `view_${body.affiliate_id}`,
		tenant_id: "",
		created_at: v.assigned_at ?? 0,
		updated_at: v.last_activity_at ?? 0,
	};
}

export async function remove(affiliateId: string): Promise<{ removed: boolean }> {
	return unassign(affiliateId);
}
